/**
 * store/api/index.ts
 * desc: root RTK Query api — every slice extends it via api.injectEndpoints
 * note: the base query wraps request(), so token refresh, API_URL prefixing and
 *       ApiRequestError handling stay in one place; `silent` skips the error toast, for every error or for the
 *       listed statuses only
 */
import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import request from '../../utilities/request';
import { showErrorMessage } from '../../utilities';
import ApiRequestError, { type ApiErrorJson } from '../../utilities/error';

export interface BaseQueryArgs {
    url: string;
    method?: string;
    body?: unknown;
    params?: NonNullable<ConstructorParameters<typeof URLSearchParams>[0]>;
    silent?: boolean | number[];
}

export type BaseQueryError = ApiErrorJson | { message: string };

const customBaseQuery: BaseQueryFn<BaseQueryArgs, unknown, BaseQueryError> = async ({ url, method = 'GET', body, params, silent }) => {
    try {
        const opts = {
            method,
            // a multipart body goes through as is — request() then leaves its Content-Type to the browser
            ...(body ? { body: body instanceof FormData ? body : JSON.stringify(body) } : {}),
        };

        // Add query parameters if provided
        const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
        const fullUrl = `${url}${queryString}`;

        const result = await request(fullUrl, opts);
        return { data: result };
    } catch (error) {
        // request() throws ApiRequestError for 4xx/5xx; anything else is a fetch/abort error
        const err = error as ApiRequestError | Error;
        const isSilent = silent === true || (Array.isArray(silent) && err instanceof ApiRequestError && silent.includes(err.statusCode));
        if (!isSilent) showErrorMessage(err);
        return { error: err instanceof ApiRequestError ? err.toJson() : { message: err.message } };
    }
};

export const api = createApi({
    reducerPath: 'api',
    baseQuery: customBaseQuery,
    tagTypes: [ 'RULES', 'RULE', 'RULE_SETS', 'RULE_SET', 'DASHBOARD_DATA', 'ENVIRONMENT_FIELD_DEFINITIONS', 'ENVIRONMENTS', 'ORDER_SCHEMAS', 'WIKI_PAGES', 'PATH_DEBUG_SESSIONS', 'PATH_DEBUG_RUN_STEPS', 'PATH_DEBUG_STEP_RUNS' ],
    endpoints: () => ({})
});

export default api;
